import type { RootStore } from '~/stores/root.store';
import { ActionName } from '../services/actions-manager/action-name';
import { Action, ServerActionType } from '../services/actions-manager/action';
import { IUpdateVariableContext } from '../types/builder.types';
import {
    calculateVariablesBaseDataForServer,
    createUpdateVariableValueCommand,
    extractOldData
} from '../utils/commands.utils';
import { Variable } from '../models/variable.model';
import { Flow } from '../models/flow.model';
import {
    IActionResult,
    IInvoiceHeaderVariableValue
} from '~/types/server.types';
import { getFormattedMemberBillingDetails } from '~/utils/block.utils';
import { ScheduleContainerBlock } from '~/models/blocks/schedule-container-block.model';

export interface IUpdateVariableOptions {
    applyResolvedCallback?: (result: IActionResult) => void;
}

export class VariableController {
    rootStore: RootStore;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }

    // #region actions
    updateVariable<VariableValue = Object>(
        flow: Flow,
        variable: Variable<VariableValue>,
        data: Partial<VariableValue>,
        options?: IUpdateVariableOptions
    ) {
        const { actionsManager } = this.rootStore;

        // Protect against a case where trying to update but it has already been deleted
        if (this.isVariableDeleted(flow.draftRevision, variable.variable_id)) {
            return;
        }

        const updateVariableAction = this.createUpdateVariableAction(
            variable,
            data,
            options
        );
        actionsManager.doNewApplyAction(updateVariableAction);
    }

    resetScheduleVariable({ flow, connectedVariable }: ScheduleContainerBlock) {
        const { calendar_item_id, calendar_item_id_pending_cancel } =
            connectedVariable.value;
        this.updateVariable(flow, connectedVariable, {
            session_config_hash: null,
            calendar_item_id: null,
            calendar_item_id_pending_cancel:
                calendar_item_id || calendar_item_id_pending_cancel
        });
    }

    isVariableDeleted(flowRevision, variableId: string) {
        return !flowRevision.variablesMap[variableId];
    }

    updateVariableLocally<T>(
        flow: Flow,
        variable: Variable<T>,
        data: T,
        safeMerge = true
    ) {
        flow.draftRevision.setVariableValue(
            variable.variable_id,
            data,
            safeMerge
        );
    }

    updateContact(memberId: string) {
        const {
            builderUiStore: { selectedFlow }
        } = this.rootStore;
        this.updateVariable<Partial<IInvoiceHeaderVariableValue>>(
            selectedFlow,
            selectedFlow.invoiceContainerBlock.invoiceHeaderBlock
                .connectedVariable,
            {
                bill_to_content: getFormattedMemberBillingDetails(
                    selectedFlow.flowRecipients.find(
                        member => member._id === memberId
                    )
                ),
                bill_to_user: memberId
            }
        );
    }

    // #endregion

    // #region action creators
    createUpdateVariableAction(
        variable: Variable,
        data: Object,
        options?: IUpdateVariableOptions
    ) {
        const { actionsManager, builderUiStore } = this.rootStore;

        return actionsManager.createAction<IUpdateVariableContext>(
            (newAction: Action<IUpdateVariableContext>) => ({
                name: ActionName.UpdateVariableData,
                serverActionType: ServerActionType.UpdateVariable,
                ctx: {
                    flow: builderUiStore.selectedFlow,
                    variableId: variable.variable_id,
                    data
                },
                createCommands: () =>
                    this.createUpdateVariablesCommands(newAction),
                onApplyToServerResolved: result => {
                    if (options?.applyResolvedCallback) {
                        options.applyResolvedCallback(result);
                    }
                },
                createApplyDataForServer: () =>
                    this.getBaseDataForServer(newAction),
                createRevertDataForServer: () =>
                    this.getRevertBaseDataForServer(newAction)
            })
        );
    }

    // #endregion

    // #region actions apply
    createUpdateVariablesCommands(action: Action<IUpdateVariableContext>) {
        const { flow, data, variableId } = action.ctx;

        const variable = flow.draftRevision.variablesMap[variableId];
        const oldData = extractOldData(data, variable.value);
        const commands = [createUpdateVariableValueCommand(variableId, data)];
        const revertCommands = [
            createUpdateVariableValueCommand(variableId, oldData)
        ];

        action.setContext({ commands, revertCommands });
    }

    // #endregion

    // #region actions get server data

    private getBaseDataForServer(action: Action<IUpdateVariableContext>) {
        const {
            ctx: { commands }
        } = action;

        return calculateVariablesBaseDataForServer(commands, action);
    }

    private getRevertBaseDataForServer(action: Action<IUpdateVariableContext>) {
        const {
            ctx: { revertCommands }
        } = action;

        return calculateVariablesBaseDataForServer(revertCommands, action);
    }

    // #endregion
}
